import { Controller, useFormContext } from 'react-hook-form';

import { InputField } from '@nesso/ui/primitives/input-field';

import { FieldReveal } from './field-reveal';

type PreferredFirstNameFormValues = {
    preferredFirstName: string | null;
};

type Props = {
    maxLength: number;
};

// Revealed on click of "Add preferred first name" or while a value is saved; the click state
// resets per record because the edit forms are keyed by it.
// `hasSavedPreferredFirstName` reads `formState.defaultValues`, not `field.value`: the live value
// would collapse the field back to the button on the keystroke that empties it, taking focus along.
// The `Controller` stays mounted and only its output swaps (unlike quick-add's conditional mount):
// the backend requires `preferredFirstName` on every save, and a missing key drops the saved name.
export function PreferredFirstNameField({ maxLength }: Props) {
    const {
        control,
        formState: { isSubmitting, defaultValues },
    } = useFormContext<PreferredFirstNameFormValues>();
    const hasSavedPreferredFirstName = !!defaultValues?.preferredFirstName;

    return (
        <Controller
            control={control}
            name="preferredFirstName"
            render={({ field, fieldState }) => (
                <FieldReveal
                    label="Add preferred first name"
                    disabled={isSubmitting}
                    hasSavedValue={hasSavedPreferredFirstName}
                >
                    {(revealedByClick) => (
                        <InputField
                            {...field}
                            value={field.value ?? ''}
                            // Seeded null, but a cleared input emits '' — which never equals null again.
                            onChange={(event) => field.onChange(event.target.value || null)}
                            id={field.name}
                            label="Preferred first name (optional)"
                            autoFocus={revealedByClick}
                            disabled={isSubmitting}
                            maxLength={maxLength}
                            error={fieldState.error?.message ?? fieldState.invalid}
                        />
                    )}
                </FieldReveal>
            )}
        />
    );
}
